import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import GroupsIcon from '@mui/icons-material/Groups'
import HomeIcon from '@mui/icons-material/Home'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { TestCompletionModeSection } from '@/components/tests/sections/TestCompletionModeSection'
import { TestAnswerFormatSection } from '@/components/tests/sections/TestAnswerFormatSection'
import { TestCheckGoalSection } from '@/components/tests/sections/TestCheckGoalSection'
import { TEACHER_LESSON_PROFILE } from '@/components/tests/testCreateProfiles'
import { readTemplateSettings, templateConditionsToRows } from '@/components/tests/templateSettings'
import { completionModeIsComplete } from '@/components/tests/testSettingsTypes'
import { useTestSettingsForm } from '@/components/tests/useTestSettingsForm'
import { TestWordFiltersSection } from '@/components/tests/sections/TestWordFiltersSection'
import { TestDisplaySection } from '@/components/tests/sections/TestDisplaySection'
import { TestTemplateSection } from '@/components/tests/sections/TestTemplateSection'
import { TestHintsSection } from '@/components/tests/sections/TestHintsSection'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { lessonsApi } from '@/api/lessons'
import { tagsApi, type Tag } from '@/api/tagsApi'
import { testsApi, type TestPreviewRunRequestPayload } from '@/api/tests'
import { isLessonRecipientGroup, isLessonRecipientUser } from '@/utils/lessonRecipient'
import { getEnums, type EnumOption } from '@/api/enums'
import { getWordAttributeFilterKeys } from '@/constants/wordAttributeFilterKeys'
import { ROUTES } from '@/utils/constants'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { getUserDisplayName } from '@/utils/userName'
import { answerTypeSupportsAutoChecking } from '@/utils/testPromptAnswerMatrix'
import type { Lesson, LessonParticipant, TestTemplate } from '@/types'
import type { Card, WordAttribute } from '@/types/cards'
import { cardsApi } from '@/api/cardsApi'
import { useAuthStore } from '@/stores/authStore'
import '@/styles/dashboard.css'

type PreviewThreshold = {
  id: number
  name: string
  progress_from: number
  progress_to: number
  /** Сколько карточек теста должно приходиться на порог (абсолютное число после расчёта на бэкенде). */
  count_cards_in_test: number
}

/** Карточка у ученика с тем же словом, что у карточки учителя; при отсутствии — создаётся по слову учителя. */
async function ensureStudentCardForWordFromTeacherCard(teacherCard: Card, studentUserId: number): Promise<number> {
  const text = (teacherCard.word?.text ?? '').trim()
  if (!text) throw new Error('Нет текста слова')
  const listing = await cardsApi.getCards({ user_id: studentUserId, search: text, per_page: 30 })
  const lower = text.toLowerCase()
  const hit = listing.data.find((c) => (c.word?.text ?? '').trim().toLowerCase() === lower)
  if (hit) return hit.id
  const attrs = (teacherCard.word?.attributes ?? [])
    .filter((a: WordAttribute) => Boolean(a.key) && String(a.value ?? '').trim() !== '')
    .map((a: WordAttribute) => ({ key: String(a.key), value: String(a.value ?? '').trim() }))
  const created = await cardsApi.createCard({
    text,
    user_id: studentUserId,
    ...(attrs.length > 0 ? { attributes: attrs } : {}),
  })
  return created.id
}

type PreviewCard = {
  id: number
  text: string
  translation: string
  relax_filters: string[]
  threshold_id: number | null
  hit_threshold: boolean
  prompt_type: string | null
  answer_type: string | null
  skill_type: string[]
  /** Квотный навык из превью (для создания теста 1:1 с preview). */
  primary_skill_type?: string | null
}

type PreviewUser = {
  user_id: number
  count_need: number
  count_found: number
  shortage: number
  threshold: PreviewThreshold[]
  cards: PreviewCard[]
}

type PreviewState = { users: PreviewUser[] }

type PreviewCardWithUser = PreviewCard & { __user_id: number }

/** Задержка перед автообновлением предпросмотра после изменения формы (мс). */
const PREVIEW_DEBOUNCE_MS = 3_000

/** Макс. число точек в навигаторе учеников группы (при большем числе учеников — равномерная разбивка). */
const PREVIEW_GROUP_MAX_DOTS = 9

function previewGroupDotTargetIndices(studentCount: number, maxDots: number): number[] {
  if (studentCount <= 0) return []
  if (studentCount <= maxDots) return Array.from({ length: studentCount }, (_, i) => i)
  const last = maxDots - 1
  if (last <= 0) return [0]
  return Array.from({ length: maxDots }, (_, j) => Math.round((j * (studentCount - 1)) / last))
}

function closestPreviewGroupDotIndex(currentStudentIndex: number, dotTargets: number[]): number {
  if (dotTargets.length === 0) return 0
  let best = 0
  let bestDist = Infinity
  dotTargets.forEach((idx, j) => {
    const d = Math.abs(idx - currentStudentIndex)
    if (d < bestDist) {
      bestDist = d
      best = j
    }
  })
  return best
}

function pickDisplayName(p: LessonParticipant): string {
  return getUserDisplayName(p.user ?? { id: p.user_id })
}

/** В теме `MuiCard` задан внешний `padding`; с `CardContent` получается двойной отступ — обнуляем на корне карточки. */
// formatPreviewCardSourceLine больше не показываем на карточках — пороги выводятся списком в PreviewPanel.

type Skill = 'translation' | 'reverse_translation' | 'spelling' | 'listening' | 'pronunciation'

/** Конструктор теста из урока: возможности формы собраны в один профиль. */
const PROFILE = TEACHER_LESSON_PROFILE

type TemplateId = number | 'none'

// teacher-preview теперь строится сервером через /api/tests/preview

function promptTypeHuman(pt: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t(`tests.run.promptTypes.${pt}`, { defaultValue: pt })
}

function answerTypeShortLabel(at: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t(`tests.run.answerTypeLabels.${at}`, { defaultValue: at })
}

function skillHuman(skill: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t(`tests.run.skillLabels.${skill}`, { defaultValue: skill })
}

/** API должен отдавать string[]; на всякий случай — строка или объект из прокси. */
function normalizePreviewSkillTypes(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s).trim()).filter(Boolean)
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return []
}

function CardBlock({
  card,
  isRelaxed,
  skillBadges: _skillBadges,
  autoCheckEnabled,
  thresholdColors,
}: {
  card: PreviewCard
  isRelaxed: boolean
  skillBadges: Skill[]
  autoCheckEnabled: boolean
  thresholdColors?: { bg: string; border: string; chipBg: string } | null
}) {
  const { t } = useTranslation()
  const ctxPrompt = card.prompt_type?.trim() || null
  const ctxAnswer = card.answer_type?.trim() || null
  const evaluatedSkills = useMemo(() => normalizePreviewSkillTypes(card.skill_type), [card.skill_type])
  const relaxCaption = useMemo(() => {
    if (!isRelaxed) return null
    const rf = card.relax_filters ?? []
    const hasTags = rf.includes('tags')
    const hasFilters = rf.includes('filters')
    if (hasTags && hasFilters) return t('tests.run.preview.relaxTagsAndFilters')
    if (hasTags) return t('tests.run.preview.relaxTags')
    if (hasFilters) return t('tests.run.preview.relaxFilters')
    return t('tests.run.preview.relaxGeneric')
  }, [isRelaxed, card.relax_filters, t])
  /** Жёлтый фон — только если послабление и карточка не в интервале порога; иначе как «по фильтрам» + подпись послабления. */
  const useWarningChrome = isRelaxed && !card.hit_threshold
  const showAutoBadge = Boolean(autoCheckEnabled && ctxAnswer && answerTypeSupportsAutoChecking(ctxAnswer))

  const denseChipSx: SxProps<Theme> = {
    height: 24,
    maxWidth: 180,
    backgroundImage: 'none',
    '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis' },
    '& .MuiChip-icon': { ml: 0.5 },
  }

  const badgeBg = thresholdColors?.chipBg ?? null

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        px: 1.25,
        py: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        bgcolor: useWarningChrome
          ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.06 : 0.12)
          : thresholdColors?.bg ?? 'background.paper',
        borderColor: useWarningChrome
          ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.35 : 0.4)
          : thresholdColors?.border ?? 'divider',
      })}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
          {card.text || `Карточка #${card.id}`}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          title={card.translation || undefined}
          sx={{ display: 'block', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
        >
          {card.translation || '—'}
        </Typography>
        {ctxPrompt || ctxAnswer || evaluatedSkills.length > 0 ? (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              mt: 0.5,
              alignItems: 'center',
              flexWrap: 'wrap',
              rowGap: 0.5,
              overflow: 'visible',
              '& > *': { flexShrink: 0 },
            }}
          >
            {ctxPrompt ? (
              <Tooltip title={t('tests.run.badges.tooltipDisplay', { value: promptTypeHuman(ctxPrompt, t) })}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                  label={promptTypeHuman(ctxPrompt, t)}
                  sx={(theme) => ({
                    ...denseChipSx,
                    backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important`,
                    '&.MuiChip-root': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                    '&.MuiChip-outlined': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                  })}
                />
              </Tooltip>
            ) : null}

            {evaluatedSkills.map((sk, idx) => (
              <Tooltip key={`${sk}-${idx}`} title={t('tests.run.badges.tooltipSkill', { value: skillHuman(sk, t) })}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<TrackChangesIcon sx={{ fontSize: 16 }} />}
                  label={skillHuman(sk, t)}
                  sx={(theme) => ({
                    ...denseChipSx,
                    backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important`,
                    '&.MuiChip-root': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                    '&.MuiChip-outlined': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                  })}
                />
              </Tooltip>
            ))}

            {ctxAnswer ? (
              <Tooltip title={t('tests.run.badges.tooltipAnswer', { value: answerTypeShortLabel(ctxAnswer, t) })}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
                  label={answerTypeShortLabel(ctxAnswer, t)}
                  sx={(theme) => ({
                    ...denseChipSx,
                    backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important`,
                    '&.MuiChip-root': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                    '&.MuiChip-outlined': { backgroundColor: `${badgeBg ?? theme.palette.background.paper} !important` },
                  })}
                />
              </Tooltip>
            ) : null}

            {showAutoBadge ? (
              <Tooltip title={t('tests.run.badges.autoTooltip')}>
                <Chip
                  size="small"
                  label={t('tests.run.badges.auto')}
                  color="success"
                  variant="outlined"
                  sx={{
                    height: 24,
                    backgroundColor: (theme) => `${badgeBg ?? theme.palette.background.paper} !important`,
                    backgroundImage: 'none',
                    '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                    '&.MuiChip-root': { backgroundColor: (theme) => `${badgeBg ?? theme.palette.background.paper} !important` },
                    '&.MuiChip-outlined': { backgroundColor: (theme) => `${badgeBg ?? theme.palette.background.paper} !important` },
                  }}
                />
              </Tooltip>
            ) : null}
          </Stack>
        ) : null}
        {relaxCaption ? (
          <Typography
            variant="caption"
            color={useWarningChrome ? 'warning.main' : 'text.secondary'}
            sx={{ fontStyle: 'italic', display: 'block', mt: 0.25 }}
          >
            {relaxCaption}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  )
}

function PreviewStatsSkeleton() {
  return (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
      <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
        <Skeleton variant="text" width={48} height={36} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="70%" height={18} />
      </Paper>
      <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
        <Skeleton variant="text" width={36} height={36} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="65%" height={18} />
      </Paper>
    </Stack>
  )
}

function PreviewCardSkeleton() {
  return (
    <Paper variant="outlined" sx={{ p: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="55%" height={22} />
        <Skeleton variant="text" width="40%" height={18} />
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, pt: 0.25 }}>
        <Skeleton variant="rounded" width={36} height={24} />
        <Skeleton variant="rounded" width={36} height={24} />
      </Stack>
    </Paper>
  )
}

function hashToHue(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  return h % 360
}

function pastelForKey(key: string): { bg: string; border: string; chipBg: string } {
  const hue = hashToHue(key)
  // Пастельные тона: высокая светлота, невысокая насыщенность.
  return {
    bg: `hsl(${hue} 65% 92%)`,
    border: `hsl(${hue} 50% 70%)`,
    // Подложка бейджей: чуть темнее, чем фон карточки.
    chipBg: `hsl(${hue} 60% 86%)`,
  }
}

function GroupPreviewStudentStrip(props: {
  users: PreviewUser[]
  participants: LessonParticipant[]
  selectedIndex: number
  onSelectIndex: (index: number) => void
}) {
  const { users, participants, selectedIndex, onSelectIndex } = props
  const n = users.length
  const dotTargets = useMemo(() => previewGroupDotTargetIndices(n, PREVIEW_GROUP_MAX_DOTS), [n])
  const activeDot = closestPreviewGroupDotIndex(selectedIndex, dotTargets)
  const uid = users[selectedIndex]?.user_id
  const participant = uid != null ? participants.find((p) => p.user_id === uid) : null
  const label = participant ? pickDisplayName(participant) : uid != null ? `#${uid}` : '—'

  const goPrev = () => onSelectIndex(Math.max(0, selectedIndex - 1))
  const goNext = () => onSelectIndex(Math.min(n - 1, selectedIndex + 1))

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 1.5,
        mb: 2,
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.04 : 0.08),
        borderColor: alpha(theme.palette.primary.main, 0.2),
      })}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <IconButton
            size="small"
            aria-label="Предыдущий ученик"
            onClick={goPrev}
            disabled={selectedIndex <= 0}
            sx={{ flexShrink: 0 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Avatar
            src={participant?.user?.avatar_url ?? undefined}
            sx={(theme) => ({
              width: 40,
              height: 40,
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
              color: 'primary.main',
              fontWeight: 700,
            })}
          >
            {label.slice(0, 1).toUpperCase()}
          </Avatar>
          <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              Ученик в группе
            </Typography>
            <Typography variant="body2" fontWeight={600} noWrap title={label}>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedIndex + 1} из {n}
            </Typography>
          </Stack>
          <IconButton
            size="small"
            aria-label="Следующий ученик"
            onClick={goNext}
            disabled={selectedIndex >= n - 1}
            sx={{ flexShrink: 0 }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Stack direction="row" justifyContent="center" alignItems="center" flexWrap="wrap" sx={{ gap: 0.75 }}>
          {dotTargets.map((studentIdx, j) => {
            const isActive = j === activeDot
            return (
              <Box
                key={j}
                component="button"
                type="button"
                onClick={() => onSelectIndex(studentIdx)}
                sx={{
                  p: 0,
                  m: 0,
                  border: 'none',
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  lineHeight: 0,
                }}
                aria-label={`Ученик ${studentIdx + 1}`}
              >
                <Box
                  sx={(theme) => ({
                    width: isActive ? 9 : 7,
                    height: isActive ? 9 : 7,
                    borderRadius: '50%',
                    bgcolor: isActive ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.35),
                    transition: 'width 0.15s, height 0.15s, background-color 0.15s',
                  })}
                />
              </Box>
            )
          })}
        </Stack>
        {n > PREVIEW_GROUP_MAX_DOTS ? (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
            До {PREVIEW_GROUP_MAX_DOTS} точек при большой группе (равномерный шаг по списку учеников)
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  )
}

function PreviewPanel(props: {
  title: string
  preview: PreviewState | null
  isLoading: boolean
  cardsLoading: boolean
  skillBadges: Skill[]
  autoCheckEnabled: boolean
  /** Когда превью намеренно не запрашивается (нет обязательных настроек слева). */
  selectionBlockHint: string | null
  /** Режим превью для теста на группу: карточки и метрики по выбранному ученику, навигатор сверху. */
  isGroupPreview: boolean
  participants: LessonParticipant[]
  groupStudentIndex: number
  onGroupStudentIndexChange: (index: number) => void
}) {
  const {
    title,
    preview,
    isLoading,
    cardsLoading,
    skillBadges,
    autoCheckEnabled,
    selectionBlockHint,
    isGroupPreview,
    participants,
    groupStudentIndex,
    onGroupStudentIndexChange,
  } = props

  const allUsers = useMemo(() => preview?.users ?? [], [preview?.users])

  const scopedUsers = useMemo(() => {
    if (!isGroupPreview || allUsers.length === 0) return allUsers
    const one = allUsers[groupStudentIndex]
    return one ? [one] : []
  }, [isGroupPreview, allUsers, groupStudentIndex])

  const allCards = useMemo<PreviewCardWithUser[]>(
    () => scopedUsers.flatMap((u) => u.cards.map((c) => ({ ...c, __user_id: u.user_id }))),
    [scopedUsers],
  )

  const relaxedCards = allCards.filter((c) => (c.relax_filters ?? []).length > 0)
  const exactCards = allCards.filter((c) => (c.relax_filters ?? []).length === 0)
  /** Одна карточка — один раз: послабление по фильтрам/тегам ИЛИ не попала в квотный «строгий» порог (например any_threshold ранее). */
  const extraCount = allCards.filter((c) => (c.relax_filters ?? []).length > 0 || !c.hit_threshold).length

  const { shortByQuota, thresholdBandRows } = useMemo(() => {
    const bandKey = (th: Pick<PreviewThreshold, 'name' | 'progress_from' | 'progress_to'>) =>
      `${(th.name ?? '').trim()}|${th.progress_from}|${th.progress_to}`
    const quotaByBand = new Map<string, number>()
    const metaByBand = new Map<
      string,
      { title: string; progress_from: number; progress_to: number; range: string }
    >()
    for (const u of scopedUsers) {
      for (const th of u.threshold ?? []) {
        const q = Math.max(0, Number(th.count_cards_in_test ?? 0))
        const key = bandKey(th)
        quotaByBand.set(key, (quotaByBand.get(key) ?? 0) + q)
        if (!metaByBand.has(key)) {
          const title = (th.name ?? '').trim() || 'Порог'
          metaByBand.set(key, {
            title,
            progress_from: th.progress_from,
            progress_to: th.progress_to,
            range: `${th.progress_from}–${th.progress_to}`,
          })
        }
      }
    }
    const strictByBand = new Map<string, number>()
    for (const c of allCards) {
      if (!c.hit_threshold) continue
      if ((c.relax_filters ?? []).length > 0) continue
      const tid = c.threshold_id
      if (!tid) continue
      const u = scopedUsers.find((x) => x.user_id === c.__user_id)
      const th = u?.threshold?.find((t) => t.id === tid)
      if (!th) continue
      const key = bandKey(th)
      strictByBand.set(key, (strictByBand.get(key) ?? 0) + 1)
    }
    let short = 0
    const rows: {
      key: string
      title: string
      range: string
      /** Заявленная квота карточек по порогу (сумма `count_cards_in_test` из API), не факт подбора. */
      declaredCardQuota: number
      /** Карточки строго в пороге: hit_threshold и без послаблений. */
      strictActual: number
      /** Отклонение от квоты: факт − норма (+ сверх квоты, − не добрано). */
      deviationFromQuota: number
      colors: { bg: string; border: string; chipBg: string }
    }[] = []
    for (const [key, declaredCardQuota] of quotaByBand) {
      const strictActual = strictByBand.get(key) ?? 0
      const deviationFromQuota = strictActual - declaredCardQuota
      short += Math.max(0, declaredCardQuota - strictActual)
      const meta = metaByBand.get(key)
      const title = meta?.title ?? 'Порог'
      const range = meta?.range ?? ''
      rows.push({
        key,
        title,
        range,
        declaredCardQuota,
        strictActual,
        deviationFromQuota,
        colors: pastelForKey(`${title}|${range}`),
      })
    }
    rows.sort((a, b) => {
      const af = metaByBand.get(a.key)?.progress_from ?? 0
      const bf = metaByBand.get(b.key)?.progress_from ?? 0
      if (af !== bf) return af - bf
      return a.title.localeCompare(b.title)
    })
    return { shortByQuota: short, thresholdBandRows: rows }
  }, [scopedUsers, allCards])
  /** Послабления / вне строгого порога + недобор по квотам полос (сколько «не дотянули» до плана по порогам). */
  const pickedExtraCount = extraCount + shortByQuota
  const hasShortage = Boolean(scopedUsers.some((u) => (u.shortage ?? 0) > 0))
  const hasRelaxed = pickedExtraCount > 0

  const totals = useMemo(() => {
    const need = scopedUsers.reduce((acc, u) => acc + (u.count_need ?? 0), 0)
    const found = scopedUsers.reduce((acc, u) => acc + (u.count_found ?? 0), 0)
    const shortage = scopedUsers.reduce((acc, u) => acc + Math.max(0, u.shortage ?? 0), 0)
    return { count_need: need, count_found: found, shortage }
  }, [scopedUsers])

  const thresholdMetaByUserAndId = useMemo(() => {
    const m = new Map<string, PreviewThreshold>()
    for (const u of scopedUsers) {
      for (const th of u.threshold ?? []) {
        if (typeof th?.id === 'number') m.set(`${u.user_id}:${th.id}`, th)
      }
    }
    return m
  }, [scopedUsers])

  const thresholdColorByCardKey = useMemo(() => {
    const out = new Map<string, { bg: string; border: string; chipBg: string }>()
    for (const c of allCards) {
      if (!c.hit_threshold) continue
      const tid = c.threshold_id
      if (!tid) continue
      const th = thresholdMetaByUserAndId.get(`${c.__user_id}:${tid}`)
      if (!th) continue
      const title = (th.name ?? '').trim() || 'Порог'
      const key = `${title}|${th.progress_from}–${th.progress_to}`
      out.set(`${c.__user_id}:${c.id}`, pastelForKey(key))
    }
    return out
  }, [allCards, thresholdMetaByUserAndId])

  return (
    <Paper variant="outlined" sx={{ p: 2, position: { md: 'sticky' }, top: { md: 16 } }}>
      {!isLoading && isGroupPreview && preview && allUsers.length > 0 ? (
        <GroupPreviewStudentStrip
          users={allUsers}
          participants={participants}
          selectedIndex={groupStudentIndex}
          onSelectIndex={onGroupStudentIndexChange}
        />
      ) : null}
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>

      {isLoading ? (
        <Stack spacing={0} sx={{ mb: 0 }}>
          <PreviewStatsSkeleton />
          <Box sx={{ maxHeight: 500, overflow: 'hidden' }}>
            <Stack spacing={0.75}>
              {Array.from({ length: 5 }, (_, i) => (
                <PreviewCardSkeleton key={i} />
              ))}
            </Stack>
          </Box>
        </Stack>
      ) : (
        <>
          {/* Статистика */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
              <Typography
                variant="h5"
                sx={(theme) => ({ fontFamily: 'monospace', lineHeight: 1.1, color: theme.palette.primary.main })}
              >
                {preview ? totals.count_need : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                слов в тесте
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 1.25,
                flex: 1,
                bgcolor: hasRelaxed
                  ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.07 : 0.14)
                  : 'transparent',
              })}
            >
              <Typography
                variant="h5"
                sx={(theme) => ({
                  fontFamily: 'monospace',
                  lineHeight: 1.1,
                  color: hasRelaxed ? theme.palette.warning.dark : theme.palette.text.disabled,
                })}
              >
                {preview ? pickedExtraCount : '—'}
              </Typography>
              <Typography variant="caption" color={hasRelaxed ? 'warning.main' : 'text.disabled'}>
                добрано
              </Typography>
            </Paper>
          </Stack>

          {/* Список порогов, которые попали в карточки */}
          {thresholdBandRows.length > 0 ? (
            <Stack spacing={0.75} sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.8 }}>
                Пороги в подборе
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {thresholdBandRows.map((th) => {
                  const d = th.deviationFromQuota
                  const deviationSuffix = d === 0 ? '' : ` ${d > 0 ? '+' : ''}${d}`
                  const tip = `Квота по порогу (заявлено): ${th.declaredCardQuota}. Строго подобрано в порог: ${th.strictActual}. Отклонение: ${d === 0 ? '0' : `${d > 0 ? '+' : ''}${d}`}.`
                  return (
                    <Chip
                      key={th.key}
                      size="small"
                      title={tip}
                      label={`${th.title} · ${th.range} (${th.declaredCardQuota})${deviationSuffix}`}
                      variant="filled"
                      sx={{
                        backgroundColor: th.colors.bg,
                        backgroundImage: 'none',
                        borderColor: th.colors.border,
                        color: 'text.primary',
                        '& .MuiChip-label': { fontSize: '0.7rem' },
                      }}
                    />
                  )
                })}
              </Stack>
            </Stack>
          ) : null}

          {/* Алерт: добрано */}
          {hasRelaxed && preview ? (
            <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
              По фильтрам найдено {Math.max(0, totals.count_found - pickedExtraCount)} из {totals.count_need}. Добрано{' '}
              {pickedExtraCount} — с учётом послаблений, карточек вне строгого порога и недобора по квотам полос порогов.
            </Alert>
          ) : null}

          {/* Алерт: нехватка */}
          {hasShortage && preview ? (
            <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
              Не хватает {totals.shortage} карточек. Тест может быть неполным.
            </Alert>
          ) : null}

          {!preview ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
              {selectionBlockHint ??
                `После изменения параметров список обновится автоматически через ${PREVIEW_DEBOUNCE_MS / 1000} секунд.`}
            </Typography>
          ) : null}

          {/* Список карточек */}
          {(allCards.length > 0 || cardsLoading) && preview ? (
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
              {cardsLoading ? (
                <Stack spacing={0.75}>
                  {Array.from({ length: Math.min(6, Math.max(allCards.length, 3)) }, (_, i) => (
                    <PreviewCardSkeleton key={i} />
                  ))}
                </Stack>
              ) : (
                <Stack spacing={0.75}>
                  {exactCards.length > 0 && (hasRelaxed || relaxedCards.length > 0) ? (
                    <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 2 }}>
                      По фильтрам ({exactCards.length})
                    </Typography>
                  ) : null}
                  {exactCards.map((c) => (
                    <CardBlock
                      key={`${c.__user_id}:${c.id}`}
                      card={c}
                      isRelaxed={false}
                      skillBadges={skillBadges}
                      autoCheckEnabled={autoCheckEnabled}
                      thresholdColors={thresholdColorByCardKey.get(`${c.__user_id}:${c.id}`) ?? null}
                    />
                  ))}

                  {relaxedCards.length > 0 ? (
                    <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 2, mt: 0.5 }}>
                      Добрано ({relaxedCards.length})
                    </Typography>
                  ) : null}
                  {relaxedCards.map((c) => (
                    <CardBlock
                      key={`${c.__user_id}:${c.id}`}
                      card={c}
                      isRelaxed={true}
                      skillBadges={skillBadges}
                      autoCheckEnabled={autoCheckEnabled}
                      thresholdColors={thresholdColorByCardKey.get(`${c.__user_id}:${c.id}`) ?? null}
                    />
                  ))}

                  {exactCards.length === 0 && relaxedCards.length === 0
                    ? allCards.map((c) => (
                        <CardBlock
                          key={`${c.__user_id}:${c.id}`}
                          card={c}
                          isRelaxed={false}
                          skillBadges={skillBadges}
                          autoCheckEnabled={autoCheckEnabled}
                          thresholdColors={thresholdColorByCardKey.get(`${c.__user_id}:${c.id}`) ?? null}
                        />
                      ))
                    : null}
                </Stack>
              )}
            </Box>
          ) : null}
        </>
      )}
    </Paper>
  )
}

export function LessonTestCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const authRoles = useAuthStore((s) => s.user?.roles ?? [])
  const canUseCustomTestTemplates = useAuthStore((s) => {
    const v = s.user?.subscription_parameters?.custom_test_templates_enabled
    return v !== 'false'
  })
  const { lessonId } = useParams()
  const id = useMemo(() => Number(lessonId), [lessonId])
  const isStudentRole = useMemo(() => authRoles.some((r) => r.slug === 'student'), [authRoles])

  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [, setIsLessonLoading] = useState(false)
  const [participants, setParticipants] = useState<LessonParticipant[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [enumOptions, setEnumOptions] = useState<Record<string, EnumOption[]>>({})
  const wordAttributeKeys = useMemo(() => getWordAttributeFilterKeys(enumOptions), [enumOptions])
  const [templates, setTemplates] = useState<TestTemplate[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateId>('none')
  const isApplyingTemplateRef = useRef(false)
  const lastAppliedTemplateSignatureRef = useRef<string | null>(null)
  /** Пока false — не переводим выбор в «Без шаблона» при изменении полей (избегаем гонки с первичным apply «Базовый»). */
  const templateListBootstrapDoneRef = useRef(false)

  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false)
  const [saveTemplateTitle, setSaveTemplateTitle] = useState('')
  const [saveTemplateDescription, setSaveTemplateDescription] = useState('')
  const [isTemplateSaving, setIsTemplateSaving] = useState(false)

  const [targetUserId, setTargetUserId] = useState<number | ''>('')

  const [explicitCardIds, setExplicitCardIds] = useState<number[]>([])
  const [explicitCardLabels, setExplicitCardLabels] = useState<Record<number, string>>({})
  const [explicitCardOptions, setExplicitCardOptions] = useState<Card[]>([])
  const [explicitCardInput, setExplicitCardInput] = useState('')
  const [explicitCardLoading, setExplicitCardLoading] = useState(false)
  const [explicitCardEnsureError, setExplicitCardEnsureError] = useState<string | null>(null)


  /** Ранний доступ: за сколько минут до начала урока тест откроется ученику (0..30). */
  // Настройки конструктора — общий хук: те же поля и производные величины
  // использует форма самостоятельного теста.
  const {
    enabledPromptTypes,
    setEnabledPromptTypes,
    togglePromptType,
    enabledAnswerFormatModes,
    setEnabledAnswerFormatModes,
    autoCheckEnabled,
    setAutoCheckEnabled,
    compatibleAnswerTypes,
    granularEnabledAnswerTypes,
    derivedActiveSkills,
    tagIds,
    setTagIds,
    attributeConditions,
    setAttributeConditions,
    relaxFilters: relaxTagFilter,
    setRelaxFilters: setRelaxTagFilter,
    mode,
    setMode,
    countNeed,
    setCountNeed,
    durationMin,
    setDurationMin,
    seriesTarget,
    setSeriesTarget,
    allowHints,
    setAllowHints,
    allowTranscription,
    setAllowTranscription,
    excludeDuplicateWords,
    setExcludeDuplicateWords,
  } = useTestSettingsForm(PROFILE)

  const [prepMinutesBeforeStart, setPrepMinutesBeforeStart] = useState<number | ''>(0)
  /** Режимы формата ответа в UI; при авто-проверке без отметок в API уходит пустой `enabled_answer_type`. */
  /** Пул типов отображения; для карточки бэкенд выбирает случайную допустимую пару с навыком. */

  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [preview, setPreview] = useState<PreviewState | null>(null)
  const [previewCardsLoading, setPreviewCardsLoading] = useState(false)
  const [groupPreviewStudentIndex, setGroupPreviewStudentIndex] = useState(0)

  const previewUsersKey = useMemo(() => (preview?.users ?? []).map((u) => u.user_id).join(','), [preview?.users])

  useEffect(() => {
    const n = preview?.users?.length ?? 0
    if (n <= 0) {
      setGroupPreviewStudentIndex(0)
      return
    }
    setGroupPreviewStudentIndex((i) => Math.min(i, n - 1))
  }, [previewUsersKey, preview?.users?.length])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const previewTimerRef = useRef<number | null>(null)
  const hasInitialPreviewRef = useRef(false)

  const currentTemplateSignature = useMemo(() => {
    const sortedTagIds = [...tagIds].sort((a, b) => a - b)
    const sortedPrompts = [...enabledPromptTypes].slice().sort()
    const sortedModes = [...enabledAnswerFormatModes].slice().sort()
    const normalizedAttributeConds = attributeConditions
      .map((c) => ({
        field: c.field,
        operator: c.operator,
        value: (c.value ?? '').toString(),
      }))
      .sort((a, b) => {
        const ak = `${a.field}|${a.operator}|${a.value}`
        const bk = `${b.field}|${b.operator}|${b.value}`
        return ak.localeCompare(bk)
      })

    // Сигнатура должна отражать только поля, которые реально меняются при applyTemplate.
    return JSON.stringify({
      exclude_duplicate_words: Boolean(excludeDuplicateWords),
      mode,
      count_need: countNeed,
      duration_min: durationMin,
      series_target: seriesTarget,
      enabled_prompt_type: sortedPrompts,
      enabled_answer_format_modes: sortedModes,
      allow_hints: Boolean(allowHints),
      allow_transcription: Boolean(allowTranscription),
      auto_check_enabled: Boolean(autoCheckEnabled),
      filters: {
        tag_ids: sortedTagIds,
        relax_filter: Boolean(relaxTagFilter),
        attribute_conditions: normalizedAttributeConds,
      },
      explicit_card_ids: [...explicitCardIds].slice().sort((a, b) => a - b),
    })
  }, [
    excludeDuplicateWords,
    mode,
    countNeed,
    durationMin,
    seriesTarget,
    enabledPromptTypes,
    enabledAnswerFormatModes,
    allowHints,
    allowTranscription,
    autoCheckEnabled,
    tagIds,
    relaxTagFilter,
    explicitCardIds,
    attributeConditions,
  ])

  /**
   * Применение шаблона: общий разбор настроек + сигнатура будущего состояния.
   *
   * Сигнатуру считаем до setState — эффект «ручное изменение → Без шаблона»
   * иначе снял бы только что выбранный шаблон.
   */
  const applyTemplate = (tpl: TestTemplate) => {
    const patch = readTemplateSettings(tpl, {
      promptTypes: PROFILE.promptTypes,
      answerFormatModes: PROFILE.answerFormatModes,
      attributeKeys: wordAttributeKeys,
    })
    const conditions = templateConditionsToRows(patch.attributeConditions ?? [])

    isApplyingTemplateRef.current = true
    setSelectedTemplate(tpl.id)

    const next = {
      excludeDuplicateWords: patch.excludeDuplicateWords ?? excludeDuplicateWords,
      mode: patch.mode ?? mode,
      countNeed: patch.countNeed ?? countNeed,
      durationMin: patch.durationMin ?? durationMin,
      seriesTarget: patch.seriesTarget ?? seriesTarget,
      enabledPromptTypes: patch.promptTypes ?? enabledPromptTypes,
      enabledAnswerFormatModes: patch.answerFormatModes ?? enabledAnswerFormatModes,
      allowHints: patch.allowHints ?? allowHints,
      allowTranscription: patch.allowTranscription ?? allowTranscription,
      autoCheckEnabled: patch.autoCheckEnabled ?? autoCheckEnabled,
      tagIds: patch.tagIds ?? tagIds,
      relaxTagFilter: patch.relaxFilters ?? relaxTagFilter,
      explicitCardIds: patch.explicitCardIds ?? explicitCardIds,
      attributeConditions: patch.attributeConditions ? conditions : attributeConditions,
    }

    lastAppliedTemplateSignatureRef.current = JSON.stringify({
      exclude_duplicate_words: Boolean(next.excludeDuplicateWords),
      mode: next.mode,
      count_need: next.countNeed,
      duration_min: next.durationMin,
      series_target: next.seriesTarget,
      enabled_prompt_type: [...next.enabledPromptTypes].sort(),
      enabled_answer_format_modes: [...next.enabledAnswerFormatModes].sort(),
      allow_hints: Boolean(next.allowHints),
      allow_transcription: Boolean(next.allowTranscription),
      auto_check_enabled: Boolean(next.autoCheckEnabled),
      filters: {
        tag_ids: [...next.tagIds].sort((a, b) => a - b),
        relax_filter: Boolean(next.relaxTagFilter),
        attribute_conditions: next.attributeConditions
          .map((c) => ({ field: c.field, operator: c.operator, value: (c.value ?? '').toString() }))
          .sort((a, b) =>
            `${a.field}|${a.operator}|${a.value}`.localeCompare(`${b.field}|${b.operator}|${b.value}`),
          ),
      },
      explicit_card_ids: [...next.explicitCardIds].sort((a, b) => a - b),
    })

    if (patch.excludeDuplicateWords !== undefined) setExcludeDuplicateWords(patch.excludeDuplicateWords)
    if (patch.mode) setMode(patch.mode)
    if (patch.countNeed !== undefined) setCountNeed(patch.countNeed)
    if (patch.durationMin !== undefined) setDurationMin(patch.durationMin)
    if (patch.seriesTarget !== undefined) setSeriesTarget(patch.seriesTarget)
    if (patch.promptTypes) setEnabledPromptTypes(patch.promptTypes)
    if (patch.answerFormatModes) setEnabledAnswerFormatModes(patch.answerFormatModes)
    if (patch.allowHints !== undefined) setAllowHints(patch.allowHints)
    if (patch.allowTranscription !== undefined) setAllowTranscription(patch.allowTranscription)
    if (patch.autoCheckEnabled !== undefined) setAutoCheckEnabled(patch.autoCheckEnabled)
    if (patch.tagIds) setTagIds(patch.tagIds)
    if (patch.relaxFilters !== undefined) setRelaxTagFilter(patch.relaxFilters)
    if (patch.explicitCardIds) setExplicitCardIds(patch.explicitCardIds)
    if (patch.attributeConditions) setAttributeConditions(conditions)

    // Сбрасываем после commit + useEffect, иначе эффект «ручное изменение → Без шаблона» успевает сработать раньше.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        isApplyingTemplateRef.current = false
      })
    })
  }

  useEffect(() => {
    if (!Number.isFinite(id)) return
    let isMounted = true
    setIsLessonLoading(true)
    lessonsApi
      .getById(id)
      .then((l) => {
        if (!isMounted) return
        setLesson(l)
      })
      .catch((e) => {
        if (!isMounted) return
        setError(getErrorMessage(e))
      })
      .finally(() => {
        if (!isMounted) return
        setIsLessonLoading(false)
      })

    lessonsApi
      .getParticipants(id)
      .then((ps) => {
        if (!isMounted) return
        setParticipants(ps)
        if (ps.length && targetUserId === '') setTargetUserId(ps[0].user_id)
      })
      .catch(() => {})

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    tagsApi.getList({ per_page: 100 }).then((r) => setTags(r.data)).catch(() => {})
    getEnums().then(setEnumOptions).catch(() => {})
  }, [])

  useEffect(() => {
    let cancelled = false
    setTemplatesLoading(true)
    testsApi
      .listTemplates()
      .then((list) => {
        if (cancelled) return
        const allowCustom =
          useAuthStore.getState().user?.subscription_parameters?.custom_test_templates_enabled !== 'false'
        setTemplates(allowCustom ? list : list.filter((x) => x.is_system))

        // По умолчанию: "Базовый" системный шаблон.
        const base = list.find((x) => x.slug === 'base') ?? list.find((x) => x.is_system) ?? null
        if (base && selectedTemplate === 'none') {
          applyTemplate(base)
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setTemplatesLoading(false)
          window.requestAnimationFrame(() => {
            templateListBootstrapDoneRef.current = true
          })
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const targetScope = useMemo<'student' | 'group'>(() => {
    return isLessonRecipientGroup(lesson?.recipient_type) ? 'group' : 'student'
  }, [lesson?.recipient_type])

  /** Согласовано с API preview: макс. 100; для группы >2 человек — ещё floor(200 / N) на ученика. */
  const previewCountNeedInputMax = useMemo(() => {
    if (targetScope !== 'group') return 100
    const n = participants.length
    if (n <= 2) return 100
    return Math.min(100, Math.floor(200 / n))
  }, [targetScope, participants.length])

  useEffect(() => {
    if (countNeed === '' || typeof countNeed !== 'number') return
    if (countNeed > previewCountNeedInputMax) {
      setCountNeed(previewCountNeedInputMax)
    }
  }, [previewCountNeedInputMax, countNeed, setCountNeed])

  const resolvedTargetUserId = useMemo<number | null>(() => {
    if (targetScope !== 'student') return null
    if (typeof targetUserId === 'number' && Number.isFinite(targetUserId)) return targetUserId
    if (participants[0]?.user_id) return participants[0].user_id
    return isLessonRecipientUser(lesson?.recipient_type) ? Number(lesson?.recipient_id) : null
  }, [targetScope, targetUserId, participants, lesson])

  const previewRepresentativeUser = useMemo(() => {
    if (!preview?.users?.length) return null
    if (targetScope === 'group') {
      const n = preview.users.length
      const idx = Math.min(Math.max(0, groupPreviewStudentIndex), Math.max(0, n - 1))
      return preview.users[idx] ?? null
    }
    if (typeof resolvedTargetUserId === 'number' && Number.isFinite(resolvedTargetUserId)) {
      return preview.users.find((u) => u.user_id === resolvedTargetUserId) ?? preview.users[0] ?? null
    }
    return preview.users[0] ?? null
  }, [preview, targetScope, groupPreviewStudentIndex, resolvedTargetUserId])

  const previewCardsForCreate = useMemo(() => {
    const cards = previewRepresentativeUser?.cards
    if (!cards?.length) return undefined
    const rows = cards
      .filter((c) => typeof c.prompt_type === 'string' && c.prompt_type !== '' && typeof c.answer_type === 'string' && c.answer_type !== '')
      .map((c) => ({
        id: c.id,
        threshold_id: c.threshold_id,
        hit_threshold: Boolean(c.hit_threshold),
        prompt_type: c.prompt_type as string,
        answer_type: c.answer_type as string,
        primary_skill_type: (c.skill_type?.length ? c.skill_type[0] : null) ?? null,
        skill_type: normalizePreviewSkillTypes(c.skill_type),
      }))
    return rows.length > 0 ? rows : undefined
  }, [previewRepresentativeUser])

  /** Только id — иначе новый объект preview после каждого ответа пересобирает settings и зацикливает debounce-preview. */
  const previewUserIdForPreviewApi = previewRepresentativeUser?.user_id ?? null

  useEffect(() => {
    const q = explicitCardInput.trim()
    if (q.length < 2) {
      setExplicitCardOptions([])
      return
    }
    const timer = window.setTimeout(() => {
      setExplicitCardLoading(true)
      void cardsApi
        .getCards({ search: q, per_page: 25 })
        .then((res) => setExplicitCardOptions(res.data ?? []))
        .catch(() => setExplicitCardOptions([]))
        .finally(() => setExplicitCardLoading(false))
    }, 320)
    return () => window.clearTimeout(timer)
  }, [explicitCardInput])

  const handlePickExplicitTeacherCard = useCallback(
    async (_e: unknown, value: Card | null) => {
      setExplicitCardEnsureError(null)
      setExplicitCardInput('')
      if (!value) return
      if (targetScope === 'group') {
        setExplicitCardEnsureError(t('tests.createForm.explicitCards.groupUnavailable'))
        return
      }
      if (typeof resolvedTargetUserId !== 'number' || !Number.isFinite(resolvedTargetUserId)) {
        setExplicitCardEnsureError(t('tests.createForm.explicitCards.pickStudent'))
        return
      }
      try {
        const sid = await ensureStudentCardForWordFromTeacherCard(value, resolvedTargetUserId)
        const label = (value.word?.text ?? '').trim() || `№${sid}`
        setExplicitCardIds((prev) => (prev.includes(sid) ? prev : [...prev, sid]))
        setExplicitCardLabels((prev) => ({ ...prev, [sid]: label }))
      } catch (e) {
        setExplicitCardEnsureError(getErrorMessage(e))
      }
    },
    [targetScope, resolvedTargetUserId, t],
  )

  const resolvedTargetLabel = useMemo(() => {
    if (targetScope === 'group') {
      return `Группа #${lesson?.recipient_id ?? '—'}`
    }
    const byParticipant = participants.find((p) => p.user_id === resolvedTargetUserId)
    if (byParticipant) return pickDisplayName(byParticipant)
    return resolvedTargetUserId ? `Ученик #${resolvedTargetUserId}` : '—'
  }, [targetScope, lesson, participants, resolvedTargetUserId])

  /** Участник для аватара в правой колонке (индивидуальный урок). */
  const lessonContextParticipant = useMemo(() => {
    if (targetScope === 'group') return null
    if (typeof resolvedTargetUserId === 'number' && Number.isFinite(resolvedTargetUserId)) {
      return participants.find((p) => p.user_id === resolvedTargetUserId) ?? null
    }
    return participants[0] ?? null
  }, [targetScope, resolvedTargetUserId, participants])

  const getTemplateSettingsPayload = useCallback((): Record<string, unknown> => {
    const attributeConditionsFiltered = attributeConditions
      .filter((c) => c.operator === 'has' || c.operator === 'has_not' || c.value.trim() !== '')
      .map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value.trim(),
      }))

    const completion_method =
      mode === 'series'
        ? { mode: 'series' as const, series_target: String(seriesTarget) }
        : mode === 'timed'
          ? { mode: 'timed' as const, duration_min: String(durationMin) }
          : { mode: 'classic' as const }

    return {
      exclude_duplicate_words: targetScope === 'group' ? Boolean(excludeDuplicateWords) : false,
      enabled_prompt_type: enabledPromptTypes,
      enabled_answer_type: granularEnabledAnswerTypes,
      auto_check_enabled: autoCheckEnabled,
      filters: {
        tag_ids: tagIds,
        relax_filter: relaxTagFilter,
        attribute_conditions: attributeConditionsFiltered,
      },
      completion_method,
      help_settings: {
        allow_hints: allowHints,
        allow_transcription: allowTranscription,
      },
      explicit_card_ids: [...explicitCardIds],
      ...(countNeed === '' ? {} : { count_cards: countNeed }),
    }
  }, [
    targetScope,
    excludeDuplicateWords,
    mode,
    countNeed,
    durationMin,
    seriesTarget,
    enabledPromptTypes,
    granularEnabledAnswerTypes,
    allowHints,
    allowTranscription,
    autoCheckEnabled,
    tagIds,
    relaxTagFilter,
    explicitCardIds,
    attributeConditions,
  ])

  const previewRequestPayload = useMemo((): TestPreviewRunRequestPayload | null => {
    if (targetScope === 'group') {
      if (!isLessonRecipientGroup(lesson?.recipient_type) || lesson?.recipient_id == null) return null
      if (!Number.isFinite(Number(lesson.recipient_id))) return null
    } else if (typeof resolvedTargetUserId !== 'number' || !Number.isFinite(resolvedTargetUserId)) {
      return null
    }

    const target_type = targetScope === 'group' ? 'group' : 'user'
    const target_id =
      target_type === 'group' ? Number(lesson!.recipient_id) : (resolvedTargetUserId as number)

    const attributeConditionsFiltered = attributeConditions
      .filter(
        (c) => c.operator === 'has' || c.operator === 'has_not' || c.value.trim() !== '',
      )
      .map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value.trim(),
      }))

    const completion_method =
      mode === 'series'
        ? { mode: 'series' as const, series_target: String(seriesTarget) }
        : mode === 'timed'
          ? { mode: 'timed' as const, duration_min: String(durationMin) }
          : { mode: 'classic' as const }

    return {
      ...(Number.isFinite(id) && id > 0 ? { lesson_id: id } : {}),
      target_id,
      target_type,
      exclude_duplicate_words: targetScope === 'group' ? Boolean(excludeDuplicateWords) : false,
      enabled_prompt_type: enabledPromptTypes as string[],
      enabled_answer_type: granularEnabledAnswerTypes,
      auto_check_enabled: autoCheckEnabled,
      filters: {
        tag_ids: tagIds,
        relax_filter: relaxTagFilter,
        attribute_conditions: attributeConditionsFiltered,
      },
      completion_method,
      help_settings: {
        allow_hints: allowHints,
        allow_transcription: allowTranscription,
      },
      explicit_card_ids: [...explicitCardIds],
      ...(countNeed === '' ? {} : { count_cards: countNeed }),
    }
  }, [
    id,
    targetScope,
    lesson,
    resolvedTargetUserId,
    excludeDuplicateWords,
    enabledPromptTypes,
    granularEnabledAnswerTypes,
    autoCheckEnabled,
    tagIds,
    relaxTagFilter,
    attributeConditions,
    explicitCardIds,
    mode,
    seriesTarget,
    durationMin,
    countNeed,
    allowHints,
    allowTranscription,
  ])

  /** Тело `settings` для POST `/api/tests` — как превью + teacher-only поля снимка. */
  const lessonCreateSettingsPayload = useMemo(() => {
    if (!previewRequestPayload) return null
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { lesson_id, ...settingsBody } = previewRequestPayload
    return {
      ...settingsBody,
      ...(typeof previewUserIdForPreviewApi === 'number' && Number.isFinite(previewUserIdForPreviewApi)
        ? { preview_user_id: previewUserIdForPreviewApi }
        : {}),
      ...(previewCardsForCreate ? { preview_cards: previewCardsForCreate } : {}),
    }
  }, [previewRequestPayload, previewUserIdForPreviewApi, previewCardsForCreate])

  const handleTemplateChange = (tpl: TemplateId) => {
    if (tpl === 'none') {
      lastAppliedTemplateSignatureRef.current = null
      setSelectedTemplate('none')
      return
    }
    const found = templates.find((x) => x.id === tpl)
    if (found) applyTemplate(found)
  }

  useEffect(() => {
    if (!templateListBootstrapDoneRef.current) return
    if (isApplyingTemplateRef.current) return
    if (selectedTemplate === 'none') return
    // Если текущие настройки всё ещё совпадают с последним применённым шаблоном — не сбрасываем выбор.
    if (lastAppliedTemplateSignatureRef.current === currentTemplateSignature) return

    // Любое ручное изменение параметров — уходим в "Без шаблона".
    lastAppliedTemplateSignatureRef.current = null
    setSelectedTemplate('none')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTemplateSignature,
  ])

  const canPreview = useMemo(() => {
    if (targetScope === 'group') return true
    return typeof resolvedTargetUserId === 'number' && Number.isFinite(resolvedTargetUserId)
  }, [targetScope, resolvedTargetUserId])

  /** Без этих условий запрос превью не имеет смысла или даёт пустой/неконсистентный набор типов ответа. */
  const canRunPreview = useMemo(() => {
    if (!canPreview) return false
    if (!previewRequestPayload) return false
    if (enabledPromptTypes.length === 0) return false
    if (compatibleAnswerTypes.length === 0) return false
    if (autoCheckEnabled && enabledAnswerFormatModes.length === 0) return false
    // Без числа у «На время» и «Серии» сервер отбивает и превью, и создание, а
    // подстановка нуля молча собрала бы тест на ноль минут.
    if (!completionModeIsComplete(mode, durationMin, seriesTarget)) return false
    return true
  }, [
    canPreview,
    previewRequestPayload,
    enabledPromptTypes.length,
    compatibleAnswerTypes.length,
    autoCheckEnabled,
    enabledAnswerFormatModes.length,
    mode,
    durationMin,
    seriesTarget,
  ])

  const previewSelectionBlockHint = useMemo(() => {
    if (!canPreview || canRunPreview) return null
    return t('tests.createForm.previewNeedDisplayAndFormatHint', {
      display: t('tests.createForm.sectionDisplay'),
      answerFormat: t('tests.createForm.sectionAnswerFormat'),
    })
  }, [canPreview, canRunPreview, t])

  const handlePreview = async () => {
    if (!previewRequestPayload) return
    setError(null)
    setIsPreviewLoading(true)
    setPreviewCardsLoading(true)
    try {
      const res = await testsApi.preview(previewRequestPayload)
      const users = Array.isArray(res.users) ? res.users : []
      setPreview({
        users: users.map((u) => ({
          ...u,
          cards: (u.cards ?? []).map((c) => ({
            ...c,
            skill_type: normalizePreviewSkillTypes(c.skill_type),
          })),
        })),
      })
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setIsPreviewLoading(false)
      setPreviewCardsLoading(false)
    }
  }

  useEffect(() => {
    if (!canRunPreview) return
    if (hasInitialPreviewRef.current) return
    hasInitialPreviewRef.current = true
    void handlePreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRunPreview])

  useEffect(() => {
    if (!canRunPreview) return
    if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    previewTimerRef.current = window.setTimeout(() => {
      void handlePreview()
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRunPreview, previewRequestPayload, isStudentRole])

  useEffect(() => {
    if (canRunPreview) return
    setPreview(null)
    setIsPreviewLoading(false)
    setPreviewCardsLoading(false)
  }, [canRunPreview])

  // Карточки для предпросмотра приходят сразу в preview response.

  const handleSubmit = async () => {
    setError(null)
    setIsSubmitting(true)
    try {
      if (!lessonCreateSettingsPayload) {
        throw new Error('Недостаточно данных для создания теста')
      }
      const res = await testsApi.createFromLesson(
        id,
        lessonCreateSettingsPayload,
        prepMinutesBeforeStart === '' ? 0 : prepMinutesBeforeStart,
      )
      navigate(ROUTES.TEST_RUN(res.id))
    } catch (e) {
      // 422 «Доступно после создания кабинета ученику» и прочие отказы — текстом API
      setError(displayErrorMessage(getErrorMessage(e), t('tests.lessonCreate.createError')))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DashboardLayout>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to={ROUTES.DASHBOARD}
          underline="hover"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {t('dashboard.home')}
        </Link>
        <Link component={RouterLink} to={ROUTES.LESSONS} underline="hover" color="inherit">
          {t('lessons.title')}
        </Link>
        <Link component={RouterLink} to={ROUTES.LESSON_DETAILS(id)} underline="hover" color="inherit">
          {t('lessons.detailsTitle', { id })}
        </Link>
        <Typography color="text.primary">{t('tests.lessonCreateTitle')}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('tests.lessonCreateTitle')} · #{id}
        </Typography>
        <PageHelpPopover pageKey="lesson-tests-create" />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {/* content */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 360px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* main form */}
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={2}>
            <TestTemplateSection
              templates={templates}
              loading={templatesLoading}
              selectedTemplate={selectedTemplate}
              onTemplateChange={handleTemplateChange}
            />

            <TestDisplaySection
              options={PROFILE.promptTypes}
              selected={enabledPromptTypes}
              onToggle={togglePromptType}
              idPrefix={PROFILE.idPrefix}
            />

            <TestAnswerFormatSection
              modes={PROFILE.answerFormatModes}
              canDisableAutoCheck={PROFILE.canDisableAutoCheck}
              compatibleAnswerTypes={compatibleAnswerTypes}
              selectedModes={enabledAnswerFormatModes}
              onSelectedModesChange={setEnabledAnswerFormatModes}
              autoCheckEnabled={autoCheckEnabled}
              onAutoCheckEnabledChange={setAutoCheckEnabled}
              idPrefix={PROFILE.idPrefix}
            />

            <TestCheckGoalSection
              skills={derivedActiveSkills}
              hasCompatibleAnswerTypes={compatibleAnswerTypes.length > 0}
              excludeDuplicateWords={
                PROFILE.supportsGroups && targetScope === 'group'
                  ? { value: excludeDuplicateWords, onChange: setExcludeDuplicateWords }
                  : undefined
              }
            />

            <TestWordFiltersSection
              tags={tags}
              selectedTagIds={tagIds}
              onSelectedTagIdsChange={setTagIds}
              attributeConditions={attributeConditions}
              onAttributeConditionsChange={setAttributeConditions}
              attributeKeys={wordAttributeKeys}
              enumOptions={enumOptions}
              relaxFilters={relaxTagFilter}
              onRelaxFiltersChange={setRelaxTagFilter}
              explicitCardsSlot={
                PROFILE.supportsExplicitCards ? (
                  <>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('tests.createForm.explicitCards.title')}
                      </Typography>
                      <Autocomplete
                        options={explicitCardOptions}
                        loading={explicitCardLoading}
                        inputValue={explicitCardInput}
                        onInputChange={(_, v) => setExplicitCardInput(v)}
                        onChange={handlePickExplicitTeacherCard}
                        getOptionLabel={(o) => (o.word?.text ?? '').trim() || `№${o.id}`}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        disabled={targetScope === 'group'}
                        filterOptions={(x) => x}
                        noOptionsText={
                            explicitCardInput.trim().length < 2
                              ? t('tests.createForm.explicitCards.minChars')
                              : t('tests.createForm.explicitCards.noCards')
                          }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={t('tests.createForm.explicitCards.searchPlaceholder')}
                            fullWidth
                          />
                        )}
                      />
                      {explicitCardEnsureError ? (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          {explicitCardEnsureError}
                        </Alert>
                      ) : null}
                      {explicitCardIds.length > 0 ? (
                        <Stack direction="row" flexWrap="wrap" gap={0.75}>
                          {explicitCardIds.map((cid) => (
                            <Chip
                              key={cid}
                              size="small"
                              label={explicitCardLabels[cid] ?? `№${cid}`}
                              onDelete={() => {
                                setExplicitCardIds((prev) => prev.filter((x) => x !== cid))
                                setExplicitCardLabels((prev) => {
                                  const next = { ...prev }
                                  delete next[cid]
                                  return next
                                })
                              }}
                            />
                          ))}
                        </Stack>
                      ) : null}
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                        {t('tests.createForm.explicitCards.hint')}
                      </Typography>
                    </Stack>
                  </>
                ) : null
              }
            />

            <TestCompletionModeSection
              mode={mode}
              onModeChange={setMode}
              countNeed={countNeed}
              onCountNeedChange={setCountNeed}
              countNeedMax={previewCountNeedInputMax}
              groupParticipantsCount={targetScope === 'group' ? participants.length : 0}
              durationMin={durationMin}
              onDurationMinChange={setDurationMin}
              seriesTarget={seriesTarget}
              onSeriesTargetChange={setSeriesTarget}
            />

            <TestHintsSection
              allowHints={allowHints}
              onAllowHintsChange={setAllowHints}
              transcription={
                PROFILE.supportsTranscription
                  ? { value: allowTranscription, onChange: setAllowTranscription }
                  : undefined
              }
              prepMinutes={
                PROFILE.supportsPrepMinutes
                  ? { value: prepMinutesBeforeStart, onChange: setPrepMinutesBeforeStart }
                  : undefined
              }
            />
          </Stack>
        </Box>

        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              {targetScope === 'group' ? (
                <Avatar
                  sx={(theme) => ({
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
                    color: 'primary.main',
                  })}
                >
                  <GroupsIcon sx={{ fontSize: 22 }} />
                </Avatar>
              ) : (
                <Avatar
                  src={lessonContextParticipant?.user?.avatar_url ?? undefined}
                  sx={(theme) => ({
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
                    color: 'primary.main',
                    fontWeight: 700,
                  })}
                >
                  {(lessonContextParticipant ? pickDisplayName(lessonContextParticipant) : resolvedTargetLabel).slice(0, 1).toUpperCase()}
                </Avatar>
              )}
              <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <strong>Урок:</strong> #{id}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <strong>Тип:</strong> {lesson?.lesson_scope ?? '—'}
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap title={resolvedTargetLabel}>
                  <strong>Кому:</strong> {resolvedTargetLabel}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            fullWidth
            sx={{ textTransform: 'none', height: 40 }}
            disabled={!canPreview || !canRunPreview || isSubmitting}
          >
            {isSubmitting ? t('common.saving') : t('tests.actions.create')}
          </Button>
          {selectedTemplate === 'none' && !isStudentRole && canUseCustomTestTemplates ? (
            <Button
              onClick={() => setIsSaveTemplateOpen(true)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ textTransform: 'none', height: 40 }}
              disabled={!canPreview || !canRunPreview || isSubmitting}
            >
              Сохранить как шаблон
            </Button>
          ) : null}
          <PreviewPanel
            title="Предпросмотр карточек"
            preview={preview}
            isLoading={isPreviewLoading}
            cardsLoading={previewCardsLoading}
            skillBadges={derivedActiveSkills}
            autoCheckEnabled={autoCheckEnabled}
            selectionBlockHint={previewSelectionBlockHint}
            isGroupPreview={targetScope === 'group'}
            participants={participants}
            groupStudentIndex={groupPreviewStudentIndex}
            onGroupStudentIndexChange={setGroupPreviewStudentIndex}
          />
        </Stack>
      </Box>

      <Dialog
        open={isSaveTemplateOpen}
        onClose={() => {
          if (isTemplateSaving) return
          setIsSaveTemplateOpen(false)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Сохранить как шаблон</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <TextField
              label="Название"
              size="small"
              value={saveTemplateTitle}
              onChange={(e) => setSaveTemplateTitle(e.target.value)}
              autoFocus
              required
              fullWidth
            />
            <TextField
              label="Описание (опционально)"
              size="small"
              value={saveTemplateDescription}
              onChange={(e) => setSaveTemplateDescription(e.target.value)}
              fullWidth
            />
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              Будут сохранены текущие параметры теста (проверки, режим, фильтры, подсказки).
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsSaveTemplateOpen(false)}
            disabled={isTemplateSaving}
            sx={{ textTransform: 'none' }}
          >
            Отмена
          </Button>
          <Button
            onClick={async () => {
              const title = saveTemplateTitle.trim()
              if (title.length < 2) return
              setError(null)
              setIsTemplateSaving(true)
              try {
                const created = await testsApi.createTemplate({
                  title,
                  description: saveTemplateDescription.trim() ? saveTemplateDescription.trim() : null,
                  settings: getTemplateSettingsPayload(),
                })
                setTemplates((prev) => [...prev, created])
                setSelectedTemplate(created.id)
                setIsSaveTemplateOpen(false)
                setSaveTemplateTitle('')
                setSaveTemplateDescription('')
              } catch (e) {
                setError(getErrorMessage(e))
              } finally {
                setIsTemplateSaving(false)
              }
            }}
            variant="contained"
            disabled={isTemplateSaving || saveTemplateTitle.trim().length < 2}
            sx={{ textTransform: 'none' }}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}

